(ns god-ui.main
  "The window: one conversation, kept in this browser and nowhere else. The
  server stores none of it; every question carries the conversation so far
  (cut to size there, see god.limits/conversation), and the answer is
  written into the page as it arrives."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require ["react-dom/client" :as rdom]
            [clojure.string :as str]
            [helix.hooks :as hooks]
            [kc.core :as kc]))

;;; ------------------------------------------------------------ words

(def lang
  "The first of the browser's languages we speak, German or English;
  English when it prefers neither."
  (or (some (fn [l] (#{"de" "en"} (subs (str/lower-case (str l)) 0 2)))
            (or (some-> js/navigator .-languages seq)
                [(some-> js/navigator .-language)]))
      "en"))

(def de? (= "de" lang))

(def words
  (if de?
    {:new "Neues Gespräch"
     :placeholder "Frag, was du wirklich wissen willst."
     :send "Senden"
     :empty "Ich bin da."
     :empty-sub "Frag nach dem Leben, dem Tod, dem Leid, dem Ich, dem Sinn."
     :note "Hier spricht ein KI-Sprachmodell in einer Rolle. Es ersetzt keinen Menschen."
     :too-long "Zu lang"
     :limit {:exhausted "Für heute ist alles gesagt. Morgen wieder."
             :crowded "Gerade sprechen zu viele zugleich. Gleich noch einmal."
             :busy "Eine Antwort ist noch unterwegs."
             :per-minute "Langsamer. Eine Minute Stille."
             :per-hour "Genug für diese Stunde."
             :per-day "Genug für heute."}
     :failed "Keine Antwort gekommen. Noch einmal?"
     :offline "Der Server antwortet nicht."
     :retry "Noch einmal"}
    {:new "New conversation"
     :placeholder "Ask what you really want to know."
     :send "Send"
     :empty "I am here."
     :empty-sub "Ask about life, death, suffering, the self, meaning."
     :note "An AI language model speaks here in a role. It is no substitute for a person."
     :too-long "Too long"
     :limit {:exhausted "Everything has been said for today. Come back tomorrow."
             :crowded "Too many are speaking at once. Try again in a moment."
             :busy "An answer is still on its way."
             :per-minute "Slower. A minute of silence."
             :per-hour "Enough for this hour."
             :per-day "Enough for today."}
     :failed "No answer came. Again?"
     :offline "The server does not answer."
     :retry "Again"}))

(def max-chars 1500)

(def version
  "The build, as bin/release stamped it into index.html."
  (some-> (js/document.querySelector "meta[name=god-build]") (.getAttribute "content")))

;;; ------------------------------------------------------------ the conversation

(def storage-key "god.conversation")

(defn- load-messages
  "The conversation as left by the last visit. A build that recorded chats
  stored {:id :messages}; its messages are kept, the id dropped."
  []
  (let [v (try (some-> (js/localStorage.getItem storage-key) kc/read-edn)
               (catch :default _ nil))
        v (if (map? v) (:messages v) v)]
    (if (vector? v) v [])))

(defonce !chat (atom {:messages (load-messages) :pending? false :error nil}))

(defn- save! [chat]
  (try (js/localStorage.setItem storage-key (pr-str (:messages chat)))
       (catch :default _ nil)))

(add-watch !chat ::save
           ;; Not while an answer streams in, and then once it has: when an
           ;; answer ends, the messages have often already stopped changing -
           ;; only :pending? flips.
           (fn [_ _ old new]
             (when (and (not (:pending? new))
                        (or (:pending? old) (not= (:messages old) (:messages new))))
               (save! new))))

(defonce !abort (atom nil))

(defn new-conversation! []
  (some-> @!abort .abort)
  (reset! !chat {:messages [] :pending? false :error nil}))

(defn- append-answer! [text]
  (swap! !chat update :messages
         (fn [ms]
           (let [last-m (peek ms)]
             (if (= "assistant" (:role last-m))
               (conj (pop ms) (update last-m :text str text))
               (conj ms {:role "assistant" :text text}))))))

(defn- finish! [error]
  (swap! !chat (fn [c]
                 (let [ms     (:messages c)
                       empty? (and (= "assistant" (:role (peek ms))) (str/blank? (:text (peek ms))))]
                   (assoc c :pending? false :error error
                          :messages (if empty? (pop ms) ms))))))

(defn- read-lines
  "Feed every NDJSON line of a streamed body to `f`, as a map."
  [^js body f]
  (let [reader  (.getReader body)
        decoder (js/TextDecoder.)
        !buf    (atom "")]
    (letfn [(eat! [s]
              (let [parts (str/split (str @!buf s) #"\n" -1)]
                (reset! !buf (peek parts))
                (doseq [l (pop parts) :when (not (str/blank? l))]
                  (f (js->clj (js/JSON.parse l) :keywordize-keys true)))))
            (step []
              (.then (.read reader)
                     (fn [^js r]
                       (if (.-done r)
                         (eat! "\n")
                         (do (eat! (.decode decoder (.-value r) #js {:stream true}))
                             (step))))))]
      (step))))

(defn- ask!
  "Send the conversation as it stands, ending with the user's question."
  []
  (let [ctl (js/AbortController.)]
    (reset! !abort ctl)
    (swap! !chat assoc :pending? true :error nil)
    (-> (js/fetch "/api/chat"
                  #js {:method "POST"
                       :headers #js {"content-type" "application/json"
                                     "accept" "application/json"}
                       :body (js/JSON.stringify (clj->js {:messages (:messages @!chat)}))
                       :signal (.-signal ctl)})
        (.then (fn [^js resp]
                 (if (.-ok resp)
                   (let [!error (atom nil)]
                     (-> (read-lines (.-body resp)
                                     (fn [{:keys [text error]}]
                                       (when text (append-answer! text))
                                       (when error (reset! !error :failed))))
                         (.then #(finish! @!error))))
                   (-> (.json resp)
                       (.catch (fn [_] nil))
                       (.then (fn [^js body]
                                (let [reason (some-> body .-reason keyword)]
                                  (finish! (if (= 429 (.-status resp))
                                             (or reason :failed)
                                             :failed)))))))))
        (.catch (fn [^js e]
                  (when-not (= "AbortError" (.-name e))
                    (finish! :offline)))))))

(defn send! [text]
  (let [text (str/trim text)]
    (when (and (seq text) (<= (count text) max-chars) (not (:pending? @!chat)))
      (swap! !chat update :messages conj {:role "user" :text text})
      (ask!))))

(defn retry!
  "Ask the last question again. Whatever half answer came stays dropped."
  []
  (swap! !chat update :messages
         (fn [ms] (if (= "assistant" (:role (peek ms))) (pop ms) ms)))
  (when (= "user" (:role (peek (:messages @!chat))))
    (ask!)))

;;; ------------------------------------------------------------ the window

(defn- error-text [error]
  (or (get-in words [:limit error])
      (get words error)
      (:failed words)))

(def-ui Message [role text]
  ($ :div {:className (str "msg " role)}
     (for [[i p] (map-indexed vector (str/split text #"\n{2,}"))]
       ($ :p {:key i} p))))

(def-ui Conversation [messages pending? error]
  (let [end (hooks/use-ref nil)]
    (hooks/use-effect
     [messages pending?]
     (some-> @end (.scrollIntoView #js {:block "end"})))
    ($ :main {:className "conversation"}
       (if (and (empty? messages) (not pending?))
         ($ :div {:className "empty"}
            ($ :p {:className "empty-main"} (:empty words))
            ($ :p {:className "empty-sub"} (:empty-sub words)))
         ($ :<>
            (for [[i {:keys [role text]}] (map-indexed vector messages)]
              ($ Message {:key i :role role :text text}))
            (when (and pending? (not= "assistant" (:role (peek messages))))
              ($ :div {:className "msg assistant breathing"} ($ :p "…")))))
       (when error
         ($ :div {:className "error"}
            ($ :span (error-text error))
            (when-not (#{:exhausted :per-hour :per-day :busy} error)
              ($ :button {:className "link" :onClick (fn [_] (retry!))} (:retry words)))))
       ($ :div {:ref end}))))

(def-ui Composer [pending?]
  (let [[text set-text] (kc/use-state "")
        too-long?       (> (count text) max-chars)
        submit          (fn []
                          (when-not (or pending? too-long? (str/blank? text))
                            (send! text)
                            (set-text "")))]
    ($ :form {:className "composer"
              :onSubmit (fn [^js e] (.preventDefault e) (submit))}
       ($ :textarea {:value text
                     :rows 1
                     :placeholder (:placeholder words)
                     :aria-label (:placeholder words)
                     :onChange (fn [^js e] (set-text (.. e -target -value)))
                     :onKeyDown (fn [^js e]
                                  (when (and (= "Enter" (.-key e)) (not (.-shiftKey e))
                                             (not (.. e -nativeEvent -isComposing)))
                                    (.preventDefault e)
                                    (submit)))})
       (when (> (count text) (- max-chars 200))
         ($ :span {:className (str "count" (when too-long? " over"))}
            (str (count text) " / " max-chars)))
       ($ :button {:type "submit" :disabled (or pending? too-long? (str/blank? text))
                   :aria-label (:send words)}
          ($ :svg {:viewBox "0 0 24 24" :width 20 :height 20 :fill "currentColor" :aria-hidden true}
             ($ :path {:d "M12 4l-7 7h4.5v9h5v-9H19z"}))))))

(def-ui Root []
  (let [{:keys [messages pending? error]} (kc/use-atom !chat)]
    ($ :div {:className "app"}
       ($ :header
          ($ :span {:className "title"} "God")
          (when (or (seq messages) error)
            ($ :button {:className "link" :onClick (fn [_] (new-conversation!))} (:new words))))
       ($ Conversation {:messages messages :pending? pending? :error error})
       ($ :footer
          ($ Composer {:pending? pending?})
          ($ :p {:className "note"} (:note words)
             (when version
               ($ :span {:className "version"} (str " Version " version))))))))

(defonce ROOT
  (delay
    (let [element (js/document.getElementById "root")]
      (kc/assert element ::root-element-missing)
      (rdom/createRoot element))))

(defn render! []
  (-> @ROOT (.render ($ Root))))

(defn after-load! [] (render!))

(defn- install-worker! []
  (when-let [sw (some-> js/navigator .-serviceWorker)]
    (-> (.register sw "/sw.js" #js {:scope "/"})
        (.catch (fn [e] (kc/log-error "service worker not registered" e))))))

(defn main! []
  (set! (.-lang js/document.documentElement) lang)
  ;; A conversation left mid-answer by a reload is finished as it stands.
  (swap! !chat assoc :pending? false)
  (install-worker!)
  (render!))
